/**
 * TreeRenderer — the tidy-hierarchy lens.
 *
 * Renders a nested node tree as a horizontal (left-to-right) tidy tree using
 * d3-hierarchy. Links are smooth horizontal cubic curves (curveBumpX); nodes
 * are circles sized by value (sqrt scale) and colored by their top-level branch
 * via the theme palette (the root is a neutral/accent anchor). Labels sit to
 * the right of each node with a background-colored halo so they stay legible.
 *
 * Like every lens it reads a single Theme object; the dark "constellation"
 * theme is luminous marks on near-black, the editorial theme is ink marks with
 * hairline borders on a warm field. All layout math is done arithmetically so
 * the renderer runs cleanly under jsdom (no getBBox / getComputedTextLength).
 */

import { hierarchy, tree, type HierarchyPointNode } from "d3-hierarchy";
import { scaleSqrt } from "d3-scale";
import { line, curveBumpX } from "d3-shape";
import { select, type Selection } from "d3-selection";

import { THEMES, type Theme } from "../theme.js";
import type {
  LensExample,
  LensRenderer,
  LensRendererOptions,
} from "../lens.js";

/** Nested hierarchy node — each lens owns its own data shape. */
export interface TreeNode {
  label: string;
  /** Optional metric; drives node radius via a sqrt scale. */
  value?: number;
  children?: TreeNode[];
}

const SVG_NS = "http://www.w3.org/2000/svg";

export class TreeRenderer implements LensRenderer {
  private readonly svg: Selection<SVGSVGElement, unknown, null, undefined>;
  private readonly width: number;
  private readonly height: number;
  private readonly theme: Theme;
  private readonly showLabels: boolean;
  private root: TreeNode | null = null;

  constructor(
    container: SVGSVGElement | HTMLElement,
    options: LensRendererOptions = {},
  ) {
    this.width = options.width ?? 960;
    this.height = options.height ?? 720;
    this.theme =
      typeof options.theme === "object"
        ? options.theme
        : THEMES[options.theme ?? "dark"];
    this.showLabels = options.showLabels ?? true;

    if (container instanceof SVGSVGElement) {
      this.svg = select(container);
    } else {
      const svgEl = document.createElementNS(SVG_NS, "svg");
      container.appendChild(svgEl);
      this.svg = select(svgEl as SVGSVGElement);
    }

    this.svg
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("viewBox", `0 0 ${this.width} ${this.height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("background", this.theme.background);
  }

  render(data: unknown): this {
    this.root = data as TreeNode;
    this.svg.selectAll("*").remove();

    // Background rect so the field travels with exported SVGs.
    this.svg
      .append("rect")
      .attr("width", this.width)
      .attr("height", this.height)
      .attr("fill", this.theme.background);

    if (!this.root) return this;

    const theme = this.theme;
    const fs = theme.label.fontSize;

    // ── layout frame ───────────────────────────────────────────────────────
    // Reserve a generous right margin for the deepest leaf labels (estimated
    // arithmetically — never measured) plus room for the node + gap.
    const root = hierarchy<TreeNode>(this.root);
    const leafLabelWidth = (n: HierarchyPointNode<TreeNode> | typeof root) =>
      labelTextWidth(n.data, fs);

    // Widest label among the deepest nodes determines the right margin.
    let maxLeafLabel = 0;
    root.each((n) => {
      if (!n.children || n.children.length === 0) {
        maxLeafLabel = Math.max(maxLeafLabel, leafLabelWidth(n));
      }
    });

    const radius = this.buildRadiusScale(root);
    const maxR = theme.node.radiusMax;

    const padTop = 48 + maxR;
    const padBottom = 48 + maxR;
    const padLeft = 48 + maxR + 6;
    const padRight = 48 + maxR + 14 + Math.min(maxLeafLabel, 220);

    const innerW = Math.max(this.width - padLeft - padRight, 40);
    const innerH = Math.max(this.height - padTop - padBottom, 40);

    // Horizontal tidy tree: d3 lays out [vertical, horizontal], so size is
    // [height-extent, width-extent] and we read x→vertical, y→horizontal.
    const layout = tree<TreeNode>()
      .size([innerH, innerW])
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.4));
    const laid = layout(root);

    const px = (n: HierarchyPointNode<TreeNode>) => padLeft + n.y;
    const py = (n: HierarchyPointNode<TreeNode>) => padTop + n.x;

    // Branch color: each child of the root is its own cluster color; the root
    // is neutral/accent; descendants inherit their top-level ancestor's color.
    const branchColor = this.buildBranchColor(laid);
    const colorOf = (n: HierarchyPointNode<TreeNode>): string => {
      if (n.depth === 0) return this.rootColor();
      let cur: HierarchyPointNode<TreeNode> | null = n;
      while (cur && cur.depth > 1) cur = cur.parent;
      return (cur && branchColor.get(cur)) ?? theme.neutralCluster;
    };

    const content = this.svg.append("g").attr("class", "content");

    // ── links (smooth horizontal cubics) ─────────────────────────────────────
    const linkPath = line<[number, number]>()
      .x((d) => d[0])
      .y((d) => d[1])
      .curve(curveBumpX);

    const linkG = content.append("g").attr("class", "links");
    linkG
      .selectAll("path")
      .data(laid.links())
      .join("path")
      .attr("fill", "none")
      .attr("stroke", (d) => colorOf(d.target as HierarchyPointNode<TreeNode>))
      .attr("stroke-opacity", theme.name === "dark" ? 0.32 : 0.4)
      .attr("stroke-width", (d) => {
        const t = d.target as HierarchyPointNode<TreeNode>;
        // Thicker links toward the root, tapering with depth.
        return Math.max(theme.edge.widthMin, 2.6 - t.depth * 0.45);
      })
      .attr("stroke-linecap", "round")
      .attr("d", (d) => {
        const s = d.source as HierarchyPointNode<TreeNode>;
        const t = d.target as HierarchyPointNode<TreeNode>;
        return linkPath([
          [px(s), py(s)],
          [px(t), py(t)],
        ]);
      });

    // ── nodes ────────────────────────────────────────────────────────────────
    const nodeG = content.append("g").attr("class", "nodes");
    const nodeSel = nodeG
      .selectAll<SVGGElement, HierarchyPointNode<TreeNode>>("g.node")
      .data(laid.descendants())
      .join("g")
      .attr("class", "node")
      .attr("transform", (d) => `translate(${px(d)},${py(d)})`);

    nodeSel.each((d, i, groups) => {
      const node = select(groups[i]);
      const r = radius(d.data.value ?? 0);
      const color = colorOf(d);
      const isRoot = d.depth === 0;

      // Soft colored halo behind branch heads / the root for a touch of depth.
      if (isRoot || d.depth === 1) {
        const haloFill =
          theme.glow.outerColor === "white"
            ? `rgba(255,255,255,${theme.glow.haloOpacity})`
            : withAlpha(color, theme.glow.haloOpacity);
        node
          .append("circle")
          .attr("r", r * theme.glow.outerScale + 2)
          .attr("fill", haloFill);
      }

      const core = node
        .append("circle")
        .attr("r", r)
        .attr("fill", color)
        .attr("fill-opacity", theme.node.opacity);

      // Hairline border (editorial) keeps marks crisp on the warm field.
      if (theme.node.stroke) {
        core
          .attr("stroke", theme.node.stroke)
          .attr("stroke-width", theme.node.strokeWidth ?? 1);
      } else if (isRoot) {
        // On dark, give the root a faint ring so it reads as the anchor.
        core
          .attr("stroke", "rgba(255,255,255,0.35)")
          .attr("stroke-width", 1);
      }
    });

    // ── labels ───────────────────────────────────────────────────────────────
    if (this.showLabels) {
      const labelG = content.append("g").attr("class", "labels");
      labelG
        .selectAll<SVGTextElement, HierarchyPointNode<TreeNode>>("text")
        .data(laid.descendants())
        .join("text")
        .attr("x", (d) => px(d) + radius(d.data.value ?? 0) + 6)
        .attr("y", (d) => py(d))
        .attr("dominant-baseline", "middle")
        .attr("font-family", theme.label.fontFamily)
        .attr("font-size", (d) => (d.depth === 0 ? fs + 2 : fs))
        .attr("font-weight", (d) => (d.depth <= 1 ? 600 : 400))
        .attr("fill", theme.label.color)
        // Background halo (paint-order:stroke) like ForceRenderer's labels.
        .attr("paint-order", "stroke")
        .attr("stroke", theme.background)
        .attr("stroke-width", 2.5)
        .attr("stroke-linejoin", "round")
        .each((d, i, groups) => {
          const t = select(groups[i]);
          t.append("tspan").text(d.data.label);
          if (d.data.value !== undefined) {
            t.append("tspan")
              .attr("fill", theme.label.valueColor)
              .attr(
                "font-family",
                theme.label.valueFontFamily ?? theme.label.fontFamily,
              )
              .attr("dx", 4)
              .text(formatValue(d.data.value));
          }
        });
    }

    return this;
  }

  /** Static layout — nothing to settle. */
  settle(_ticks?: number): this {
    return this;
  }

  exportSVG(): string {
    const node = this.svg.node();
    if (!node) return "";
    const clone = node.cloneNode(true) as SVGSVGElement;
    return new XMLSerializer().serializeToString(clone);
  }

  destroy(): void {
    this.svg.selectAll("*").remove();
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private buildRadiusScale(
    root: ReturnType<typeof hierarchy<TreeNode>>,
  ): (v: number) => number {
    const values: number[] = [];
    root.each((n) => {
      if (n.data.value !== undefined) values.push(n.data.value);
    });
    const minV = values.length ? Math.min(...values) : 0;
    const maxV = values.length ? Math.max(...values, 1) : 1;
    // Modest range so the tidy structure stays the focus, not the bubbles.
    const scale = scaleSqrt()
      .domain([Math.min(minV, 0), maxV])
      .range([7, Math.min(this.theme.node.radiusMax, 22)]);
    return (v: number) => scale(v);
  }

  /** Assign each top-level branch (child of root) a palette color. */
  private buildBranchColor(
    laid: HierarchyPointNode<TreeNode>,
  ): Map<HierarchyPointNode<TreeNode>, string> {
    const map = new Map<HierarchyPointNode<TreeNode>, string>();
    const branches = laid.children ?? [];
    // Rank branches by subtree weight so the biggest get the strongest colors.
    const weight = (n: HierarchyPointNode<TreeNode>): number => {
      let w = n.data.value ?? 0;
      (n.children ?? []).forEach((c) => (w += weight(c)));
      return w;
    };
    const ranked = [...branches].sort((a, b) => weight(b) - weight(a));
    ranked.forEach((b, i) => {
      const color =
        i < this.theme.maxColoredClusters
          ? this.theme.palette[i]
          : this.theme.neutralCluster;
      map.set(b, color);
    });
    return map;
  }

  private rootColor(): string {
    // Accent the root: editorial uses its navy palette[0]; dark uses neutral so
    // the colored branches pop against it.
    return this.theme.name === "editorial"
      ? this.theme.palette[0]
      : this.theme.neutralCluster;
  }
}

// ── pure helpers ──────────────────────────────────────────────────────────────

/** Arithmetic label-width estimate (char count × fontSize × ~0.6). */
function labelTextWidth(d: TreeNode, fs: number): number {
  const valueLen = d.value !== undefined ? String(formatValue(d.value)).length + 2 : 0;
  return (d.label.length + valueLen) * fs * 0.6;
}

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  return v < 1 ? `${Math.round(v * 100)}%` : v.toFixed(1);
}

/** Add an alpha channel to a #rrggbb color → rgba(). Falls back to the input. */
function withAlpha(hex: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

// ── examples ──────────────────────────────────────────────────────────────────

function totemArchitecture(): TreeNode {
  return {
    label: "Totem Protocol",
    value: 100,
    children: [
      {
        label: "Identity",
        value: 58,
        children: [
          { label: "MetaTotem Root", value: 30 },
          { label: "Personas", value: 22 },
          { label: "Org Graph", value: 16 },
        ],
      },
      {
        label: "Memory",
        value: 72,
        children: [
          { label: "System State", value: 28 },
          { label: "Project Registry", value: 26 },
          {
            label: "Insights",
            value: 34,
            children: [
              { label: "Append Log", value: 14 },
              { label: "Session FIFO", value: 12 },
            ],
          },
        ],
      },
      {
        label: "Agents",
        value: 80,
        children: [
          { label: "Orchestrator", value: 40 },
          { label: "Subagents", value: 30 },
          { label: "Letta Bridge", value: 24 },
        ],
      },
      {
        label: "Lenses",
        value: 46,
        children: [
          { label: "Force", value: 20 },
          { label: "Sankey", value: 16 },
          { label: "Tree", value: 14 },
          { label: "Radar", value: 12 },
        ],
      },
    ],
  };
}

function contentTaxonomy(): TreeNode {
  return {
    label: "Content Flywheel",
    value: 100,
    children: [
      {
        label: "Source",
        value: 64,
        children: [
          { label: "Obsidian Vault", value: 38 },
          { label: "Meeting Notes", value: 22 },
          { label: "Voice Dictation", value: 16 },
        ],
      },
      {
        label: "Synthesis",
        value: 70,
        children: [
          { label: "InfraNodus Graph", value: 32 },
          {
            label: "Insight Notes",
            value: 30,
            children: [
              { label: "Gaps", value: 12 },
              { label: "Bridges", value: 11 },
            ],
          },
        ],
      },
      {
        label: "Production",
        value: 58,
        children: [
          { label: "Long-form", value: 26 },
          { label: "Short-form", value: 22 },
          { label: "Threads", value: 14 },
        ],
      },
      {
        label: "Distribution",
        value: 40,
        children: [
          { label: "Newsletter", value: 18 },
          { label: "Social", value: 16 },
        ],
      },
    ],
  };
}

function agentOrgChart(): TreeNode {
  return {
    label: "Orchestrator",
    value: 100,
    children: [
      {
        label: "Capture Team",
        value: 62,
        children: [
          { label: "Message Logger", value: 24 },
          { label: "Link Processor", value: 20 },
          { label: "Classifier", value: 18 },
        ],
      },
      {
        label: "Reasoning Team",
        value: 78,
        children: [
          { label: "Context Bridge", value: 28 },
          { label: "Action Extractor", value: 24 },
          {
            label: "Insight Synthesizer",
            value: 30,
            children: [
              { label: "Gap Minder", value: 14 },
              { label: "Roam Export", value: 11 },
            ],
          },
        ],
      },
      {
        label: "Knowledge Team",
        value: 70,
        children: [
          { label: "Claude Integration", value: 30 },
          { label: "Knowledge Graph", value: 26 },
          { label: "Content Factory", value: 20 },
        ],
      },
    ],
  };
}

export const TREE_EXAMPLES: LensExample[] = [
  {
    id: "tree-totem-architecture",
    title: "Totem Protocol Architecture",
    blurb:
      "The Totem Protocol root branching into Identity, Memory, Agents, and Lenses subsystems.",
    highlights: ["4 branches", "depth 3", "value-sized"],
    lens: "tree",
    build: () => totemArchitecture(),
  },
  {
    id: "tree-content-taxonomy",
    title: "Content Taxonomy",
    blurb:
      "How raw source material flows through synthesis and production into distribution channels.",
    highlights: ["flywheel", "taxonomy", "4 branches"],
    lens: "tree",
    build: () => contentTaxonomy(),
  },
  {
    id: "tree-agent-org-chart",
    title: "Agent Org Chart",
    blurb:
      "An orchestrator delegating to capture, reasoning, and knowledge teams of working bots.",
    highlights: ["org chart", "3 teams", "depth 4"],
    lens: "tree",
    build: () => agentOrgChart(),
  },
];
